import { useQuery } from "@tanstack/react-query";
import { Suspense, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { portalFetch } from "../../api/http";
import { missingText } from "../../i18n/texts";
import { Waiting } from "../../replies/ReplyWaiting";
import { bootstrap as readBootstrap, chatLabels, previewContext, type ChatBootstrap } from "../../replies/replyModel";
import { lazyScreen } from "../../ui/lazyScreen";

const ReplyLibraryApp = lazyScreen(() => import("../../replies/ReplyLibrary").then((module) => module.ReplyLibraryApp),
  /^\/portal\/chat\/respuestas$/);

/**
 * Respuestas rápidas (/portal/chat/respuestas), como Respuestas.razor del portal actual: el título y el texto de carga
 * salen de la página; el resto de los textos, del arranque del chat (bootstrap), igual que la isla de Blazor.
 */
export function RepliesPage({ locale }: { locale: string }) {
  const location = useLocation();
  const [initial] = useState(() => {
    const params = new URLSearchParams(location.search);
    const scope = params.get("scope");
    const usage = params.get("usage");
    const id = params.get("id");
    const parsedPage = Number(params.get("page"));
    const parsedSize = Number(params.get("size"));
    return {
      search: params.get("q") ?? "",
      scope: scope === "mine" || scope === "shared" ? scope : "all",
      usage: usage === "unused" || usage === "inactive" ? usage : "",
      selected: id && /^[0-9a-f-]{36}$/i.test(id) ? id : null,
      editing: params.get("edit")?.toLowerCase() === "true",
      page: Number.isSafeInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1,
      size: [10, 25, 50, 100].includes(parsedSize) ? parsedSize : 10,
    };
  });
  const texts = useQuery({
    queryKey: ["texts", locale, "replies-page"],
    queryFn: () =>
      portalFetch<Record<string, string>>(`/api/portal/texts/${locale}?prefixes=REPLY_TITLE,REPLY_LOADING`),
  });
  const [boot, setBoot] = useState<ChatBootstrap | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    readBootstrap(controller.signal).then(setBoot, () => undefined);
    return () => controller.abort();
  }, []);
  const title = texts.data?.REPLY_TITLE ?? missingText("REPLY_TITLE");
  useEffect(() => {
    if (texts.data) document.title = `${title} · Bheli`;
  }, [texts.data, title]);
  if (!texts.data) return null;
  if (!boot)
    return (
      <div className="reply-loading">
        <h1 className="reply-loading-title">{title}</h1>
        <Waiting label={texts.data.REPLY_LOADING ?? missingText("REPLY_LOADING")} />
      </div>
    );
  return (
    <Suspense fallback={<Waiting label={texts.data.REPLY_LOADING ?? missingText("REPLY_LOADING")} />}>
      <ReplyLibraryApp
        L={chatLabels(boot.labels)}
        locale={boot.locale}
        bootstrap={boot}
        sample={previewContext(boot)}
        initial={initial}
      />
    </Suspense>
  );
}
